import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Modal, Spinner, Alert, Toast, InputGroup, Form } from 'react-bootstrap';
import { FaTachometerAlt, FaDollarSign, FaRegChartBar, FaCog, FaWallet } from 'react-icons/fa';
import axios from 'axios';
import SuccessMessage from './SuccessMessage';

// Reusable InfoMessage Component
const InfoMessage = ({ message, variant }) => (
  <div className="form-group my-2">
    <Alert variant={variant} className="py-2">{message}</Alert>
  </div>
);



const Plan = ({ userDetails }) => {
  const [selectedPlan, setSelectedPlan] = useState('');
  const [amount, setAmount] = useState('');
  const [duration, setDuration] = useState('45'); // default 45 days
  const [expectedProfit, setExpectedProfit] = useState('');
  const [expectedWithdrawal, setExpectedWithdrawal] = useState('');
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [durationSelected, setDurationSelected] = useState(false);
  const [newBalance, setNewBalance] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const plans = [
    { name: 'Standard', type: 'Basic (power of 1)', dailyTask: 'just tap away to keep enough SVP token, you will need it, also do regular task, refer friends is advisible  ', minAmount: 10 },
    { name: 'Silver', type: 'Premium (power of 2)', dailyTask: 'just tap away to keep enough SVP token, you will need it, also do regular task, refer friends is advisible    ', minAmount: 20 },
    { name: 'Gold', type: 'Elite (power of 3)', dailyTask: 'just tap away to keep enough SVP token, you will need it, also do regular task, refer friends is advisible  ', minAmount: 50 },
    { name: 'Platinum', type: 'Master (power of 4)', dailyTask: 'just tap away to keep enough SVP token, you will need it, also do regular task , refer friends is advisible ', minAmount: 100 },
  ];



  // Calculate expectedWithdrawal and expectedProfit when amount or duration changes
  useEffect(() => {
    if (!amount || !duration) {
      setExpectedWithdrawal('');
      setExpectedProfit('');
      setNewBalance('');
      return;
    }

    const X = 0.5;
    const numericAmount = parseFloat(amount);
    const numericDuration = parseInt(duration, 10);

    let multiplier = 0;
    if (numericAmount === 10) multiplier = 1;
    else if (numericAmount === 20) multiplier = 2;
    else if (numericAmount === 50) multiplier = 4;
    else if (numericAmount === 100) multiplier = 8;
    else multiplier = 1; // fallback

    const withdrawal = X * multiplier * numericDuration;
    const profit = withdrawal - numericAmount;

    const currentBalance = parseFloat(userDetails.balance) || 0;
    let updatedBalance = currentBalance - numericAmount;
    if (updatedBalance <= 0) updatedBalance = 0;

    setExpectedWithdrawal(withdrawal.toFixed(2));
    setExpectedProfit(profit.toFixed(2));
    setNewBalance(updatedBalance.toFixed(2));
    setDurationSelected(true);
    setErrorMessage('');
  }, [amount, duration, userDetails.balance]);

  const handleMoreDetails = (plan) => {
    setSelectedPlan(plan.name);
    setSelectedPlanDetails(plan);
    setShowModal(true);
  };

  const handlePickPlan = (plan) => {
    setSelectedPlan(plan.name);
    setSelectedPlanDetails(plan);
    setAmount(plan.minAmount.toString());
    setDuration('45'); // default duration reset to 45 days
    setShowPlanModal(true);
    setDurationSelected(false);
    setErrorMessage('');
  };

  const handleDurationChange = (e) => {
    setDuration(e.target.value);
  };

  const handleClosePlanModal = () => {
    setSelectedPlan('');
    setAmount('');
    setDuration('45');
    setExpectedProfit('');
    setExpectedWithdrawal('');
    setErrorMessage('');
    setDurationSelected(false);
    setShowPlanModal(false);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleSubmit = async () => {
    setLoading(true);
    setErrorMessage('');

    if (userDetails?.tier && userDetails.tier !== 'No Plan' && userDetails.tier !== 'NULL' && userDetails.tier !== 'Loading...') {
      setErrorMessage(`You already have an active ${userDetails.tier} plan. Please wait until your current plan matures before starting a new one.`);
      setLoading(false);
      return;
    }

    const numericAmount = parseFloat(amount);
    if (!selectedPlan || !duration) {
      setErrorMessage('Please select both a plan and a duration.');
      setLoading(false);
      return;
    }

    if (isNaN(numericAmount) || numericAmount < selectedPlanDetails?.minAmount) {
      setErrorMessage(`Amount must be at least $${selectedPlanDetails?.minAmount}`);
      setLoading(false);
      return;
    }

    if (!userDetails?.email) {
      setErrorMessage('User details not found.');
      setLoading(false);
      return;
    }

    try {
      const currentBalance = parseFloat(userDetails.balance);
      const newBal = currentBalance - numericAmount;
      

      if (newBal < 0) {
        setErrorMessage('Insufficient balance.');
        setLoading(false);
        return;
      }

      const response = await axios.post(
        'https://sharedvision-partners.online/v1/API/update_balance.php',
        {
          email: userDetails.email,
          newBalance: newBal,
          duration: duration,
          selectedPlan: selectedPlan,
          expectedProfit: expectedProfit,
          expectedWithdrawal: expectedWithdrawal,
          amount: numericAmount,

        },
        { headers: { 'Content-Type': 'application/json' } }
      );




      if (response.data.success) {
        setShowToast(true);
        setShowSuccessMessage(true);
        handleClosePlanModal();
      } else {
        setErrorMessage(response.data.message);
      }
    } catch (error) {
      setErrorMessage('An error occurred while updating the balance.');
    } finally {
      setLoading(false);
    }
  };

  const {
    tier = '',
    username = ''
  } = userDetails || {};

  return (
    <div className="container my-4">
      <h2 className="mb-4">Investment Plans</h2>
      <p>Welcome, <strong>{username}</strong></p>
      <p>You are presently on <strong>{tier ?? 'No'}</strong> plan. </p>

      {showToast && (
        <Toast
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={8000}
          autohide
          className="mb-3"
          bg="info"
        >
          <Toast.Body>
            <p><strong>Note:</strong> Thank you for your investment! To support and help this platform continue to grow and bring even more value to you, remember to refer at least one person during your investment period.</p>
          </Toast.Body>
        </Toast>
      )}



<Row className="g-4">
        {plans.map((plan) => (
          <Col xs={12} sm={6} md={4} key={plan.name}>
            <Card className="shadow-sm">
              <Card.Body>
                <Card.Title>{plan.name}</Card.Title>
                <Card.Subtitle className="mb-2 text-muted">{plan.type} Plan </Card.Subtitle>
                                <Card.Text><FaDollarSign /> Amount to Investment: ${plan.minAmount}</Card.Text>
                              <Card.Text>
  <FaTachometerAlt /> <span>See More for daily task info</span>
</Card.Text>
                <Button variant="info" onClick={() => handleMoreDetails(plan)} className="mb-2 w-100">More</Button>
                <Button variant="primary" onClick={() => handlePickPlan(plan)} className="w-100">Pick Plan</Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Success Message Modal */}
      <SuccessMessage
        show={showSuccessMessage}
        onClose={() => setShowSuccessMessage(false)}
      />

      {/* More Details Modal */}
      {showModal && (
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Plan Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p><strong>Type:</strong> {selectedPlanDetails?.type}</p>
            <p><strong>Daily Task:</strong> {selectedPlanDetails?.dailyTask}</p>
            <p><strong>Minimum Amount:</strong> ${selectedPlanDetails?.minAmount}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>Close</Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Plan Pick Modal */}
      {showPlanModal && (
        <Modal show={showPlanModal} onHide={handleClosePlanModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Pick Your {selectedPlan} Plan</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="mb-3 align-items-center">
                <Form.Group as={Col} xs={12} md={6} controlId="planName">
                  <Form.Label>Plan</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaCog /></InputGroup.Text>
                    <Form.Control type="text" value={selectedPlan} readOnly />
                  </InputGroup>
                </Form.Group>

                <Form.Group as={Col} xs={12} md={6} controlId="amount">
                  <Form.Label>Amount</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaDollarSign /></InputGroup.Text>
                    <Form.Control
                      type="number"
                      value={amount}
                      readOnly
                    />
                  </InputGroup>
                </Form.Group>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Duration</Form.Label>
                <div>
                  <Form.Check
                    inline
                    label="45 Days"
                    name="durationRadios"
                    type="radio"
                    id="duration-45"
                    value="45"
                    checked={duration === '45'}
                    onChange={handleDurationChange}
                  />
                  <Form.Check
                    inline
                    label="120 Days"
                    name="durationRadios"
                    type="radio"
                    id="duration-120"
                    value="120"
                    checked={duration === '120'}
                    onChange={handleDurationChange}
                    disabled={true} // Disable this option
                  />
                </div>
              </Form.Group>

              <Row className="mb-3 align-items-center">
                <Form.Group as={Col} xs={12} md={6} controlId="expectedProfit">
                  <Form.Label>Expected Profit</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaRegChartBar /></InputGroup.Text>
                    <Form.Control type="number" value={expectedProfit} readOnly />
                  </InputGroup>
                </Form.Group>

                <Form.Group as={Col} xs={12} md={6} controlId="expectedWithdrawal">
                  <Form.Label>Expected Withdrawal</Form.Label>
                  <InputGroup>
                    <InputGroup.Text><FaWallet /></InputGroup.Text>
                    <Form.Control type="number" value={expectedWithdrawal} readOnly />
                  </InputGroup>
                </Form.Group>
              </Row>

              {(errorMessage || durationSelected) && (
                <InfoMessage
                  variant={errorMessage ? 'danger' : 'info'}
                  message={
                    errorMessage ||
                    `for a ${duration}-days plan. Expected profit is $${expectedProfit}, withdrawal: $${expectedWithdrawal}. New balance: $${newBalance}.`
                  }
                />
              )}

              <div className="d-flex justify-content-between">
                <Button variant="primary" onClick={handleSubmit} disabled={loading || !!errorMessage}>
                  {loading ? <Spinner animation="border" size="sm" /> : 'Confirm Investment'}
                </Button>
                <Button variant="secondary" onClick={handleClosePlanModal}>Close</Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      )}

      <style>{`
        /* Custom styling to keep form neat and responsive */
        .form-label {
          font-weight: 600;
        }
        .input-group-text {
          background-color: #f0f0f0;
          border-right: 0;
        }
        .form-control {
          border-left: 0;
        }
        @media (max-width: 576px) {
          .input-group-text {
            font-size: 14px;
          }
          .form-control {
            font-size: 14px;
          }
          .btn {
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default Plan;
